import { useCallback, useEffect, useRef, useState } from "react";
import { CaretDown, Desktop, Moon, Sun } from "@phosphor-icons/react";
import { useTheme } from "next-themes";

const STORAGE_KEY = "callsect-public-theme";
const SYSTEM_QUERY = "(prefers-color-scheme: dark)";
const VALID_PREFERENCES = new Set(["light", "dark", "system"]);

const OPTIONS = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Desktop },
];

function readSystemTheme() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "dark";
  return window.matchMedia(SYSTEM_QUERY).matches ? "dark" : "light";
}

function readBootResolvedTheme() {
  if (typeof document !== "undefined") {
    const bootTheme = document.documentElement.getAttribute("data-callsect-public-theme");
    if (bootTheme === "light" || bootTheme === "dark") return bootTheme;
  }
  return readSystemTheme();
}

export function usePublicMarketingTheme() {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const preference = VALID_PREFERENCES.has(theme) ? theme : "system";
  const effectiveTheme = resolvedTheme === "light" || resolvedTheme === "dark"
    ? resolvedTheme
    : readBootResolvedTheme();

  const setPreference = useCallback((nextPreference) => {
    const next = VALID_PREFERENCES.has(nextPreference) ? nextPreference : "system";
    setTheme(next);

    // Keep System as the absence of an explicit preference. next-themes keeps
    // the live React state, while reloads naturally fall back to System.
    if (typeof window !== "undefined") {
      try {
        window.localStorage.removeItem("callsect-theme");
        if (next === "system") window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Theme selection still works for the current session when storage is unavailable.
      }
    }
  }, [setTheme]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-callsect-public-theme", effectiveTheme);
    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor) themeColor.setAttribute("content", effectiveTheme === "light" ? "#f7f7fc" : "#05070b");
  }, [effectiveTheme]);

  return {
    preference,
    resolvedTheme: effectiveTheme,
    setPreference,
  };
}

export default function PublicMarketingThemeControl({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const selected = OPTIONS.find((option) => option.value === value) || OPTIONS[2];
  const ActiveIcon = selected.icon;

  const focusMenuItem = (index) => {
    const items = Array.from(menuRef.current?.querySelectorAll('[role="menuitemradio"]') || []);
    if (!items.length) return;
    const normalized = (index + items.length) % items.length;
    items[normalized]?.focus();
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    setOpen(true);
    window.requestAnimationFrame(() => {
      const selectedIndex = Math.max(0, OPTIONS.findIndex((option) => option.value === value));
      focusMenuItem(selectedIndex);
    });
  };

  const handleMenuKeyDown = (event) => {
    const items = Array.from(menuRef.current?.querySelectorAll('[role="menuitemradio"]') || []);
    if (!items.length) return;
    const currentIndex = Math.max(0, items.indexOf(document.activeElement));

    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusMenuItem(currentIndex + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusMenuItem(currentIndex - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusMenuItem(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusMenuItem(items.length - 1);
    }
  };

  useEffect(() => {
    const close = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);

  return (
    <div className="marketing-theme-control" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="marketing-theme-control__trigger"
        onClick={() => setOpen((current) => !current)}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Appearance: ${selected.label}`}
      >
        <ActiveIcon size={16} />
        <span>{selected.label}</span>
        <CaretDown size={13} className={open ? "is-open" : ""} />
      </button>

      {open ? (
        <div ref={menuRef} className="marketing-theme-control__menu" role="menu" aria-label="Appearance" onKeyDown={handleMenuKeyDown}>
          {OPTIONS.map(({ value: optionValue, label, icon: Icon }) => (
            <button
              key={optionValue}
              type="button"
              role="menuitemradio"
              aria-checked={value === optionValue}
              tabIndex={value === optionValue ? 0 : -1}
              className={value === optionValue ? "is-selected" : ""}
              onClick={() => {
                onChange(optionValue);
                setOpen(false);
                window.requestAnimationFrame(() => triggerRef.current?.focus());
              }}
            >
              <Icon size={16} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
